import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Avatar,
  Col,
  Menu,
  Row,
  Typography,
  Image,
  Upload,
  message,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import PersonalDetails from "./Components/PersonalDetails";
import ChangePassword from "./Components/ChangePassword";
import NomineeDetails from "./Components/NomineeDetails";
import BankDetails from "./Components/BankDetails";
import EscrowDetails from "./Components/EscrowDetails";
import KYCDetails from "./Components/KYCDetails";
import CompanyDetails from "./Components/CompanyDetails";
import HufDetails from "./Components/HufDetails";
import {
  uploadProfileAvatar,
  getBankDetailsByOrgId,
  getKycDetails,
} from "../../redux/slices/ProfileSlice";
import ArrowRightBlack from "../../assets/icons/arrow-right-black.svg";
import ArrowRightBlue from "../../assets/icons/arrow-right-blue.svg";
import {
  TITLE_TXT,
  USERTYPE_ID,
  GLOBAL_CONSTANTS,
} from "../../constants/constants";
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import "./Profile.scss";

const { Title, Text } = Typography;

const isKycVerifiedFromDocs = (kycDetails, isIndividual, isCompany, isNbfc, isHuf) => {
  if (!kycDetails || !Object.keys(kycDetails).length) {
    return null;
  }
  const keys = [
    "kycPanValue",
    "kycMobile",
    "kycEmail",
    "kycBank",
  ];
  if (isIndividual) {
    keys.push("kycAadhaar");
  }
  if (isCompany || isNbfc || isHuf) {
    keys.push("kycDirectorsPanValue", "kycDirectorsAadhaarValue");
  }
  if (isCompany || isNbfc) {
    keys.push("kycGstinValue");
  }

  return keys.every((key) => {
    const doc = kycDetails[key];
    return (
      doc && doc.docValue && doc.isVerified === true && doc.rejected !== true
    );
  });
};

const Profile = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { value } = location.state || {};
  const { user } = useSelector((state) => state.auth);
  const { avatarUploadLoading, bankDetailsData, kycDetailsData } = useSelector(
    (state) => state.profile,
  );

  const userType = user?.userTypeId;
  const isIndividual = userType === USERTYPE_ID.INDIVIDUAL;
  const isHuf = userType === USERTYPE_ID.HUF;
  const isCompany = userType === USERTYPE_ID.ENTITY;
  const isNbfc = userType === USERTYPE_ID.NBFC;

  const [activeComponent, setActiveComponent] = useState(value || "personal");
  const [avatarUrl, setAvatarUrl] = useState(
    localStorage.getItem("invAvatarUrl") || "",
  );

  const kycVerifiedFromApi = isKycVerifiedFromDocs(
    kycDetailsData,
    isIndividual,
    isCompany,
    isNbfc,
    isHuf,
  );
  const isKycVerified =
    kycVerifiedFromApi ??
    [
      GLOBAL_CONSTANTS.STAT_KYC_VERIFIED_ID,
      GLOBAL_CONSTANTS.STAT_ACTIVE_ID,
    ].includes(user?.userStatusId);
    
  const isBankVerifiedInKyc = kycDetailsData?.kycBank?.isVerified === true && Boolean(kycDetailsData?.kycBank?.docValue);
  const isBankLinked = (bankDetailsData && bankDetailsData.length > 0) && isBankVerifiedInKyc;

  useEffect(() => {
    if (value) setActiveComponent(value);
  }, [value]);

  useEffect(() => {
    if (user?.orgId) {
      dispatch(getBankDetailsByOrgId(user.orgId));
      dispatch(getKycDetails());
    }
  }, [user?.orgId, dispatch]);

  const handleMenuClick = (key) => setActiveComponent(key);

  const getDisplayName = () =>
    user?.EnquiryMgmtView?.busName ||
    user?.Business?.busName ||
    user?.business?.busName ||
    user?.userName ||
    "User";

  const getInitial = () => {
    const label = getDisplayName();
    return label ? label.charAt(0).toUpperCase() : "U";
  };

  const uploadAvatar = async ({ file, onSuccess, onError }) => {
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];

    if (!allowedTypes.includes(file.type)) {
      message.error("Please upload JPG, PNG, or WEBP image only");
      onError?.(new Error("Invalid file type"));
      return;
    }

    if (file.size / 1024 / 1024 > 5) {
      message.error("Image size should be less than 5MB");
      onError?.(new Error("File too large"));
      return;
    }

    try {
      const folderPath = `investor/profile/${user?.orgId || user?.userId || "common"}`;
      const res = await dispatch(uploadProfileAvatar({ file, folderPath }));

      if (uploadProfileAvatar.fulfilled.match(res)) {
        const uploadedUrl = res.payload?.fileUrl || "";
        setAvatarUrl(uploadedUrl);
        localStorage.setItem("invAvatarUrl", uploadedUrl);
        message.success("Profile image uploaded");
        onSuccess?.("ok");
      } else {
        throw new Error("Upload failed");
      }
    } catch (err) {
      message.error("Profile image upload failed");
      onError?.(err);
    }
  };

  const renderComponent = () => {
    const components = {
      personal: <PersonalDetails />,
      huf: <HufDetails />,
      company: <CompanyDetails />,
      password: <ChangePassword />,
      nominee: <NomineeDetails />,
      bank: <BankDetails />,
      escrow: <EscrowDetails />,
      kyc: <KYCDetails />,
    };
    return components[activeComponent] || <PersonalDetails />;
  };

  const menuItems = [
    {
      key: "personal",
      label: isHuf ? TITLE_TXT.KAR_DETS : TITLE_TXT.PER_DETS,
      show: true,
    },
    { key: "huf", label: TITLE_TXT.HUF_DETS, show: isHuf },
    {
      key: "company",
      label: isCompany ? TITLE_TXT.COMP_DETS : TITLE_TXT.NBFC_DETS,
      show: isCompany || isNbfc,
    },
    { key: "password", label: TITLE_TXT.CHNG_PWD_TITLE, show: true },
    { key: "nominee", label: TITLE_TXT.NOM_DETS, show: isHuf || isIndividual },
    { key: "bank", label: TITLE_TXT.BANK_DETS, show: true },
    { key: "escrow", label: TITLE_TXT.ECW_DETS, show: true },
    { key: "kyc", label: TITLE_TXT.KYC_DETS, show: true },
  ];

  const renderMenuItem = ({ key, label, show }) =>
    show && (
      <Menu.Item key={key} onClick={() => handleMenuClick(key)}>
        <Text>{label}</Text>
        <Image
          src={activeComponent === key ? ArrowRightBlue : ArrowRightBlack}
          preview={false}
          alt="ArrowRight"
        />
      </Menu.Item>
    );

  return (
    <Row gutter={[24, 16]} className="profile-container">
      <Col xs={24} lg={8}>
        <div className="profile-menu-container">
          <Upload
            accept="image/png,image/jpeg,image/jpg,image/webp"
            maxCount={1}
            showUploadList={false}
            customRequest={uploadAvatar}
            disabled={avatarUploadLoading}
          >
            <div className="profile-avatar-wrapper" role="button" tabIndex={0}>
              <Avatar
                size={100}
                src={avatarUrl || undefined}
                style={{
                  backgroundColor: "#2243B6",
                  color: "#ffffff",
                  fontSize: 42,
                  marginTop: 30,
                }}
              >
                {!avatarUrl ? getInitial() : null}
              </Avatar>
              <span className="profile-avatar-edit-icon">
                <EditOutlined />
              </span>
            </div>
          </Upload>
          <Title level={4}>{getDisplayName()}</Title>
          <div className="profile-status-badges">
            <span
              className={`profile-status-badge ${isKycVerified ? "badge-verified" : "badge-pending"}`}
            >
              {isKycVerified ? <CheckCircleFilled /> : <CloseCircleFilled />}
              {isKycVerified ? "KYC Verified" : "KYC Pending"}
            </span>
            <span
              className={`profile-status-badge ${isBankLinked ? "badge-verified" : "badge-pending"}`}
            >
              {isBankLinked ? <CheckCircleFilled /> : <CloseCircleFilled />}
              {isBankLinked ? "Bank Linked" : "Bank Pending"}
            </span>
          </div>
          <Menu
            className="menuBlock"
            mode="inline"
            selectedKeys={[activeComponent]}
          >
            {menuItems.map(renderMenuItem)}
          </Menu>
        </div>
      </Col>
      <Col xs={24} lg={16}>
        {renderComponent()}
      </Col>
    </Row>
  );
};

export default Profile;
